const FREE_SHIPPING_THRESHOLD = 10000;
const WELCOME_DISCOUNT_CODE = 'WELCOMEDELIVERY';

function hasWelcomeDiscount() {
  return sessionStorage.getItem('welcomeDiscountShown') === 'true';
}

async function updateDeliveryProgress(cartData) {
  const progress = document.querySelector('cart-drawer .fs-delivery-progress');
  if (!progress) return;

  const fill = progress.querySelector('.fs-delivery-progress__fill');
  const text = progress.querySelector('.fs-delivery-progress__text');
  const track = progress.querySelector('.fs-delivery-progress__track');

  // Check if welcome discount is active
  if (hasWelcomeDiscount()) {
    progress.classList.add('fs-delivery-progress--welcome');
    if (text) {
      text.innerHTML = `You have free delivery with your welcome discount '<strong>${WELCOME_DISCOUNT_CODE}</strong>', this will be applied at checkout.`;
    }
    if (fill) fill.style.width = '100%';
    if (track) track.style.display = 'none';
    return;
  }

  // Remove welcome class if not active
  progress.classList.remove('fs-delivery-progress--welcome');

  // If total_price is not in cartData (e.g., from add-to-cart response), fetch the cart
  let total = Number(cartData?.total_price);
  if (isNaN(total) || total === undefined) {
    try {
      const cartRes = await fetch('/cart.js');
      const fullCart = await cartRes.json();
      total = Number(fullCart.total_price) || 0;
    } catch (err) {
      console.warn('Failed to fetch cart for delivery progress:', err);
      total = 0;
    }
  }

  const remaining = Math.max(FREE_SHIPPING_THRESHOLD - total, 0);
  const percent = Math.min(Math.round((total / FREE_SHIPPING_THRESHOLD) * 100), 100);
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
  const locale = (window.Shopify && Shopify.locale) || 'en-GB';
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  if (track) track.style.display = '';
  if (fill) fill.style.width = `${percent}%`;
  if (text) {
    text.textContent =
      remaining > 0
        ? `Spend ${formatter.format(remaining / 100)} more for reduced mainland UK delivery.`
        : `Your order qualifies for reduced mainland delivery.`;
  }
}

// Check welcome discount on page load
document.addEventListener('DOMContentLoaded', function() {
  if (hasWelcomeDiscount()) {
    updateDeliveryProgress({});
  }
});

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    if (typeof subscribe === 'function') {
      this.deliveryProgressUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.cartData) updateDeliveryProgress(event.cartData);
      });
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  disconnectedCallback() {
    if (this.deliveryProgressUnsubscriber) {
      this.deliveryProgressUnsubscriber();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const emptyDrawer = this.querySelector('.drawer__inner-empty');
        const containerToTrapFocusOn =
          this.classList.contains('is-empty') && emptyDrawer
            ? emptyDrawer
            : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const isEmpty = parsedState?.item_count === 0;
    this.classList.toggle('is-empty', isEmpty);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionElement || !sectionHTML) return;

      const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (innerHTML === null) return;
      sectionElement.innerHTML = innerHTML;
    });

    updateDeliveryProgress(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Don't open drawer on mobile - toast notification is shown instead
      if (window.innerWidth >= 750) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
